import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  Divider,
  TextField,
  Select,
  MenuItem,
  Button,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import VisibilityIcon from "@mui/icons-material/Visibility";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import UIWrapper from "../../components/UIWrapper";
import ShipmentInquiriesService from "../../services/ShipmentInquiriesService";

const carrierTypes = [
  {
    value: "BIOCAIR",
    label: "BIOCAIR",
  },
  {
    value: "DHL",
    label: "DHL",
  },
  {
    value: "FedEx",
    label: "FedEx",
  },
  {
    value: "FedEx Ground",
    label: "FedEx Ground",
  },
  {
    value: "Hand Delivered",
    label: "Hand Delivered",
  },
  {
    value: "Marken",
    label: "Marken",
  },
  {
    value: "Priority Express",
    label: "Priority Express",
  },
  {
    value: "Quickstat",
    label: "Quickstat",
  },
  {
    value: "TNT",
    label: "TNT",
  },
  {
    value: "UPS",
    label: "UPS",
  },
  {
    value: "USA Courier",
    label: "USA Courier",
  },
  {
    value: "USPS",
    label: "USPS",
  },
  {
    value: "VIP Cargo",
    label: "VIP Cargo",
  },
  {
    value: "World Courrier",
    label: "World Courrier",
  },
];

const StatusCard = ({ label, count, color }) => {
  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: "10px",
        border: "1px solid #e5e7eb",
        borderLeft: `4px solid ${color}`,
        background: "#ffffff",
        p: 2,
        minWidth: "180px",
        boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
        transition: "0.2s",
        "&:hover": {
          boxShadow: "0px 3px 8px rgba(0,0,0,0.12)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Typography sx={{ fontSize: "12px", color: "#777" }}>{label}</Typography>

      <Typography sx={{ fontSize: "26px", fontWeight: "bold", mt: 0.5 }}>
        {count}
      </Typography>
    </Box>
  );
};

const getStatusChip = (status) => {
  const map = {
    pending: { color: "#1e88e5", bg: "#e3f2fd" },
    "approved & processing": { color: "#2e7d32", bg: "#e8f5e9" },
    shipped: { color: "#7c3aed", bg: "#ede9fe" },
    delivered: { color: "#2e7d32", bg: "#e8f5e9" },
    cancelled: { color: "#d32f2f", bg: "#fdecea" },
    rejected: { color: "#d32f2f", bg: "#fdecea" },
    draft: { color: "#757575", bg: "#f3f4f6" },
  };

  const key = status?.toLowerCase();
  const item = map[key] || { color: "#1976d2", bg: "#e3f2fd" };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        px: "10px",
        py: "4px",
        borderRadius: "999px",
        backgroundColor: item.bg,
        width: "fit-content",
      }}
    >
      <Box
        sx={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor: item.color,
        }}
      />
      <Typography
        sx={{
          fontSize: "12px",
          color: item.color,
          fontWeight: 500,
        }}
      >
        {status}
      </Typography>
    </Box>
  );
};

const ShipmentRequests = () => {
  const [summary, setSummary] = useState({
    drafts: 0,
    pending: 0,
    approved: 0,
    inTransit: 0,
    delivered: 0,
    rejected: 0,
    total: 0,
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tab, setTab] = useState("all");
  const [selectedCarrier, setSelectedCarrier] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [destinationSearch, setDestinationSearch] = useState("");
  const history = useHistory();

  const getTableData = () => {
    setLoading(true);

    ShipmentInquiriesService.getRequestShipmentList()
      .then((res) => {
        const list = res.data?.order_shipment_requests || [];
        const counts = res.data?.status_counts || {};

        const formatted = list.map((item) => {
          const shipment = item[0];
          const sampleCount = item[1];

          return {
            ...shipment,
            samples: sampleCount,
          };
        });

        setTableData(formatted);
        const normalized = formatted.map((row) => ({
          ...row,
          status: row.status?.toLowerCase(),
        }));

        setSummary({
          drafts: normalized.filter((r) => r.status === "draft").length,
          pending: normalized.filter((r) => r.status === "pending").length,
          approved: normalized.filter(
            (r) => r.status === "approved & processing",
          ).length,
          inTransit: normalized.filter((r) => r.status === "shipped").length,
          delivered: normalized.filter((r) => r.status === "delivered").length,
          rejected: normalized.filter((r) => r.status === "rejected").length,
          total: normalized.length,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleView = (row) => {
    history.push("/sample-distribution/approve-reject", { request: row });
  };

  const handleDraft = (row) => {
    history.push(
      `/sample-distribution/new-shipment-request?request_id=${row.request_id}`,
    );
  };

  useEffect(() => {
    getTableData();
  }, []);

  const filteredData = tableData.filter((row) => {
    const status = row.status?.toLowerCase();
    let isStatusMatch = true;

    if (tab !== "all") {
      if (tab === "drafts") isStatusMatch = status === "draft";
      if (tab === "pending") isStatusMatch = status === "pending";
      if (tab === "approved")
        isStatusMatch = status === "approved & processing";
      if (tab === "shipped") isStatusMatch = status === "shipped";
      if (tab === "rejected") isStatusMatch = status === "rejected";
    }

    let isCarrierMatch = true;

    if (selectedCarrier !== "all") {
      isCarrierMatch = row.carrier === selectedCarrier;
    }
    let isDateMatch = true;
    if (fromDate || toDate) {
      const shipDate = new Date(row.ship_date);
      if (fromDate) {
        isDateMatch = shipDate >= new Date(fromDate);
      }
      if (toDate) {
        isDateMatch = isDateMatch && shipDate <= new Date(toDate);
      }
    }

    let isSearchMatch = true;
    if (searchText) {
      isSearchMatch = row.request_id
        ?.toLowerCase()
        .includes(searchText.toLowerCase());
    }

    let isDestinationMatch = true;
    if (destinationSearch) {
      isDestinationMatch = row.destination_site
        ?.toLowerCase()
        .includes(destinationSearch.toLowerCase());
    }
    return (
      isStatusMatch &&
      isCarrierMatch &&
      isDateMatch &&
      isSearchMatch &&
      isDestinationMatch
    );
  });

  const sortedData = [...filteredData].sort((a, b) => {
    return new Date(b.created_on) - new Date(a.created_on);
  });

  const handleExportCSV = () => {
    if (!filteredData.length) return;
    const headers = [
      "Request ID",
      "Created",
      "Destination",
      "Samples",
      "Ship Date",
      "Carrier",
      "Status",
      "Submitted By",
    ];

    const rows = filteredData.map((row) => [
      row.request_id,
      row.created_on,
      row.destination,
      row.samples,
      row.ship_date,
      row.carrier,
      row.status,
      row.submitted_by,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows]
        .map((e) => e.map((val) => `"${val ?? ""}"`).join(","))
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    const fileName = `shipment_requests_${new Date().toISOString()}.csv`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setOpenSnackbar(true);
  };

  return (
    <UIWrapper title="Shipment Requests" paper>
      <Box>
        <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
          <StatusCard label="DRAFT" count={summary.drafts} color="#16a34a" />
          <StatusCard
            label="PENDING APPROVAL"
            count={summary.pending}
            color="#f59e0b"
          />
          <StatusCard
            label="APPROVED & PROCESSING"
            count={summary.approved}
            color="#1976d2"
          />
          <StatusCard
            label="SHIPPED"
            count={summary.inTransit}
            color="#7c3aed"
          />
        </Stack>
        <Box
          sx={{
            borderRadius: "10px",
            background: "#fff",
            p: 2,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Shipment Requests
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#777" }}>
                All sample shipment requests
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={handleExportCSV}>
                EXPORT CSV
              </Button>
              <Button
                variant="contained"
                onClick={() =>
                  history.push("/sample-distribution/new-shipment-request")
                }
              >
                + NEW REQUEST
              </Button>
            </Stack>
          </Stack>

          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab value="all" label={`All (${summary.total})`} />
            <Tab value="drafts" label={`Drafts (${summary.drafts})`} />
            <Tab value="pending" label={`Pending (${summary.pending})`} />
            <Tab value="approved" label={`Approved (${summary.approved})`} />
            <Tab value="shipped" label={`Shipped (${summary.inTransit})`} />
            <Tab value="rejected" label={`Rejected (${summary.rejected})`} />
          </Tabs>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={2} flexWrap="wrap" mb={2}>
            <TextField
              size="small"
              placeholder="Search Request ID"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              size="small"
              placeholder="Search Destination Site"
              value={destinationSearch}
              onChange={(e) => setDestinationSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Select
              size="small"
              value={selectedCarrier}
              onChange={(e) => setSelectedCarrier(e.target.value)}
            >
              <MenuItem value="all">All carriers</MenuItem>

              {carrierTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Ship Date From"
                value={fromDate ? dayjs(fromDate) : null}
                onChange={(newValue) =>
                  setFromDate(newValue?.format("YYYY-MM-DD") || "")
                }
                disableFuture
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{ minWidth: 180 }}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                  />
                )}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Ship Date To"
                value={toDate ? dayjs(toDate) : null}
                onChange={(newValue) =>
                  setToDate(newValue?.format("YYYY-MM-DD") || "")
                }
                disableFuture
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{ minWidth: 180 }}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                  />
                )}
              />
            </LocalizationProvider>

            <Button
              variant="outlined"
              onClick={() => {
                setSelectedCarrier("all");
                setFromDate("");
                setToDate("");
                setTab("all");
                setSearchText("");
                setDestinationSearch("");
              }}
            >
              RESET
            </Button>
          </Stack>

          <TableContainer
          
sx={{
    maxHeight: 600,
    overflowY: "auto",
  }}

          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Request ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Destination Site
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Samples</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Ship Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Carrier</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Submitted By
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Box
                        sx={{
                          py: 6,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  sortedData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.request_id}</TableCell>
                        <TableCell>{row.created_on}</TableCell>
                        <TableCell>{row.destination_site}</TableCell>
                        <TableCell>{row.samples}</TableCell>
                        <TableCell>{row.ship_date}</TableCell>
                        <TableCell>{row.carrier}</TableCell>
                        <TableCell>{getStatusChip(row.status)}</TableCell>
                        <TableCell>{row.submitted_by}</TableCell>
                        <TableCell align="center">
                          {row.status?.toLowerCase() === "draft" ? (
                            <IconButton
                              color="primary"
                              onClick={() => handleDraft(row)}
                            >
                              <EditIcon />
                            </IconButton>
                          ) : (
                            <IconButton
                              color="primary"
                              onClick={() => handleView(row)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No Record Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(+e.target.value);
                setPage(0);
              }}
            />
        </Box>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity="success"
            sx={{
              width: "100%",
              bgcolor: "#1b5e20",
              color: "#fff",
            }}
          >
            CSV downloaded successfully!
          </Alert>
        </Snackbar>
      </Box>
    </UIWrapper>
  );
};

export default ShipmentRequests;
